import React, { useState } from 'react';
import propTypes from 'prop-types';
import { useDispatch, useSelector, connect } from 'react-redux';

import FontAwesome from 'react-fontawesome';
import LoadingMessage from '~/Components/Global/Loading/LoadingMessage';
import TextInput from '~/Components/Global/Form/TextInput';
import Button from '~/Components/Global/Form/Button';
import Form from '~/Components/Global/Form/Form';
import { addAppleAccount, updateAppleAccount } from '~/Redux/Modules/Apple';
import ArtfulCheckbox from '~/Components/Global/Form/ArtfulCheckbox';
import { toggleUseCalendarTimezones } from '~/Redux/Modules/CalendarAccounts';

import './AddAppleAccount.scss';
import FeatureFlagView from '~/Components/Global/FeatureFlagView';
import { FLAG_APP_SPECIFIC_PASSWORD } from '~/Redux/Modules/FeatureFlags';
import { openModal } from '~/Redux/Modules/Modal';
import { REPORT_PROBLEM_MODAL } from '~/Config/Modals';
import CalendarList from './CalendarList';
import AddAccountForm from './AddAccountForm';

const ICloudLogo = require('./ICloudLogo.png');

const AddAppleAccount = ({ account, isFetching, onClickReportProblem }) => {
  const [instructions, setInstructions] = useState(false);
  const [newInstructions, setNewInstructions] = useState(false);
  const [secondaryInstructions, displaySecondaryInstructions] = useState(false);
  const [tipsInstructions, displayTipsInstructions] = useState(true);
  const [videoTutorial, displayVideoTutorial] = useState(false);
  const [editing, setEditing] = useState(false);
  const dispatch = useDispatch();
  const { isRegistering = isFetching, isUpdating } = useSelector(({ Apple }) => Apple);
  const [form, setForm] = useState({ email: '', password: '' });

  const addAccount = async data => {
    await dispatch(addAppleAccount(data));
    setForm({ email: '', password: '' });
  };
  const updateAccount = data => dispatch(updateAppleAccount(data));

  const updateField = ({ email, password }) => {
    setForm({
      email: email ?? form.email,
      password: password ?? form.password,
    });
  };

  const renderNewInstructions = () => {
    if (!newInstructions) return <div />;
    return (
      <div className="instructions card layered new-instructions">
        <div className="scroll-wrapper">
          <div className="card-body">
            <a
              href="#"
              className="close-new-instructions"
              onClick={() => {
                setNewInstructions(false);
                displayTipsInstructions(true);
              }}
            >
              <FontAwesome name="chevron-left" />
            </a>
            <h3 className="tips-for-success">
              <a
                className="toggle-tips"
                href="#"
                onClick={() => displayTipsInstructions(!tipsInstructions)}
              >
                <strong>Tips for success </strong>
                {!tipsInstructions ? (
                  <FontAwesome name="caret-right" className="caret-icon" />
                ) : (
                  <FontAwesome name="caret-down" className="caret-icon" />
                )}
              </a>
            </h3>
            {tipsInstructions && (
              <>
                <div className="tip-box pink">
                  <p>
                    When signing in to appleid.apple.com, do NOT use face or touch ID. Instead,
                    select &quot;use a different Apple ID&quot; to manually log in.
                  </p>
                </div>
                <div className="tip-box blue">
                  <p>
                    (If you sign in using touch or face ID, your password creation will fail, and
                    you will be re-directed to sign in manually.)
                  </p>
                </div>
                <div className="tip-box green">
                  <p>
                    To copy your app-specific password: Long press, drag the cursor to select the
                    password, and choose &quot;copy&quot;.
                  </p>
                </div>
              </>
            )}
            <h3 className="video-tutorial">
              <a
                className="toggle-video"
                href="#"
                onClick={() => displayVideoTutorial(!videoTutorial)}
              >
                <strong>Video tutorial </strong>
                {!videoTutorial ? (
                  <FontAwesome name="caret-right" className="caret-icon" />
                ) : (
                  <FontAwesome name="caret-down" className="caret-icon" />
                )}
              </a>
            </h3>
            {videoTutorial && (
              <div className="video-instructions">
                <p>
                  Watch{' '}
                  <a
                    className="apple-instructions-link"
                    target="_blank"
                    href="https://www.youtube.com/watch?v=siW3COZ6G7I"
                  >
                    <strong>this video</strong>
                  </a>{' '}
                  if you are having any trouble!
                </p>
              </div>
            )}
            <div className="apple-instructions">
              <p>
                Click{' '}
                <a
                  className="apple-instructions-link"
                  target="_blank"
                  href="https://support.apple.com/en-us/102654#generate"
                >
                  <strong>HERE</strong>
                </a>{' '}
                to create your password.
              </p>
            </div>
            <div className="tech-support">
              <p>
                Need more help? Contact us{' '}
                <a className="tech-support-link" href="#" onClick={() => onClickReportProblem()}>
                  <strong>HERE</strong>
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderInstructions = () => {
    if (!instructions) return <div />;
    return (
      <div className="instructions card layered">
        <a href="#" className="close-instructions" onClick={() => setInstructions(false)}>
          x
        </a>
        <div className="scroll-wrapper">
          <div className="card-body">
            <h1 className="apple-title">
              Syncing to Apple involves <strong>TWO</strong> Distinct steps:
            </h1>
            <h2>
              <span className="number">1.</span> Enable Two-Factor Authentication
            </h2>
            <p>
              Good news! Two-factor authentication is the default security method for most Apple
              IDs. If you need to enable it, use these{' '}
              <a
                className="toggle-instructions"
                href="#"
                onClick={() => displaySecondaryInstructions(!secondaryInstructions)}
              >
                <strong>simple instructions.</strong>{' '}
                {!secondaryInstructions ? (
                  <FontAwesome name="caret-right" />
                ) : (
                  <FontAwesome name="caret-down" />
                )}
              </a>
            </p>
            {secondaryInstructions && (
              <>
                <h3>On your iPhone or iPad:</h3>
                <ol>
                  <li>
                    Go to Settings &gt; (your name) &gt; Password (or Sign-In) &amp; Security.
                  </li>
                  <li>Tap Turn on Two-Factor Authentication.</li>
                  <li>Tap Continue.</li>
                  <li>Follow verification prompts.</li>
                </ol>
                <h3>On your Mac:</h3>
                <ol>
                  <li>
                    Go to Apple menu (<FontAwesome name="apple" />) &gt; System Settings (or System
                    Preferences)
                  </li>
                  <li>Click your name (or Apple ID) then Password & Security.</li>
                  <li>Next to Two-Factor Authentication, click Turn On.</li>
                </ol>
              </>
            )}
            <h2>
              <span className="number">2.</span> Login to Apple ID to Generate App-Specific Password
            </h2>
            <div className="text-box-container">
              <p>
                <strong>IMPORTANT</strong>
              </p>
              <p>
                When going to appleID.apple.com do NOT login with face or touch ID. Instead, select
                &quot;use a different Apple ID&quot; and manually enter your Apple ID and password.
              </p>
            </div>
            <ol>
              <li>
                Manually sign in to your Apple ID account{' '}
                <a
                  className="apple-sign-in"
                  target="_blank"
                  href="https://appleid.apple.com/account/home"
                >
                  AppleID.Apple.com.
                </a>
              </li>
              <li>
                In the Sign-In and Security section, scroll to and select App-Specific Passwords.
              </li>
              <li>
                Click the + sign to add a new password and label the password (ex: Artful Agenda).
              </li>
              <li>If prompted, confirm your Apple ID password.</li>
              <li>
                Copy the generated password and return to Artful Agenda where you will paste the
                password to sync to your iCloud account.
              </li>
            </ol>
          </div>
        </div>
      </div>
    );
  };

  const handleUpdate = async () => {
    await updateAccount({ id: account.id, ...form });
    setEditing(false);
    setForm({ email: '', password: '' });
  };

  const updateForm = () => {
    return (
      <Form handleSubmit={() => handleUpdate()} autoComplete="off">
        <FeatureFlagView
          featureName={FLAG_APP_SPECIFIC_PASSWORD}
          disabledView={
            <div className="simple-instructions">
              <h6>Please Generate an App-Specific Password</h6>
              <p>
                Due to Apple security measures, you will need to generate a one-time-use password to
                allow Apple Calendar access.
              </p>
              <p>
                <a className="show-instructions" href="#" onClick={() => setInstructions(true)}>
                  Click Here
                </a>{' '}
                for simple instructions. It&apos;s easy!
              </p>
            </div>
          }
        >
          <div className="simple-instructions new">
            <p>
              Apple requires you to create a one-time-use password for Artful Agenda to connect your
              calendar. (If you have any trouble, click{' '}
              <a
                className="show-instructions new"
                href="#"
                onClick={() => setNewInstructions(true)}
              >
                here
              </a>
              )
            </p>
            <div className="link-to-apple-instructions">
              <p>
                <a
                  className="apple-instructions"
                  target="_blank"
                  href="https://support.apple.com/en-us/102654#generate"
                >
                  Generate your app-specific password
                </a>
              </p>
            </div>
          </div>
        </FeatureFlagView>
        <TextInput
          className="minimal-input mobile-font-fix"
          type="email"
          name="email"
          value={form.email}
          placeholder="Apple ID"
          showLabel={false}
          autoComplete="new-username"
          onChange={updateField}
          isDisabled
        />
        <TextInput
          className="minimal-input mobile-font-fix"
          type="password"
          name="password"
          value={form.password}
          placeholder="App-Specific Password"
          showLabel={false}
          onChange={updateField}
          autoComplete="new-password"
        />
        <div className="actions">
          <Button
            className="center btn btn-default"
            onClick={() => setEditing(false)}
            text="Cancel"
          />
          <Button
            className="center btn btn-default"
            isFetching={isUpdating}
            type="submit"
            text="Update Account"
            actionText="Updating"
          />
        </div>
      </Form>
    );
  };

  const startEditing = () => {
    updateField({ email: account.email, password: '' });
    setEditing(true);
  };

  if (isFetching) return <LoadingMessage small messages="Checking for Apple account" />;
  return (
    <AddAccountForm logo={ICloudLogo} account={account}>
      {account ? (
        <div className="center gray">
          <h6>Success!</h6>
          <h5>{account.email}</h5>
          <h6>is now connected to Artful Agenda!</h6>
          {editing ? (
            updateForm()
          ) : (
            <>
              <CalendarList account={account} />
              <div className="use-calendar-timezones">
                <ArtfulCheckbox
                  label="Use Calendar Timezones"
                  value={account.use_calendar_timezones}
                  name="useCalendarTimezones"
                  onChange={({ useCalendarTimezones }) => {
                    console.log('useCalendarTimezones', useCalendarTimezones);
                    dispatch(
                      toggleUseCalendarTimezones({
                        ...account,
                        use_calendar_timezones: useCalendarTimezones,
                      })
                    );
                  }}
                />
              </div>
              <a className="update-account" href="#" onClick={() => startEditing()}>
                {account.error_count > 0 && !isUpdating && (
                  <FontAwesome name="exclamation-triangle" />
                )}
                Update App-Specific Password
              </a>
              <FeatureFlagView
                featureName={FLAG_APP_SPECIFIC_PASSWORD}
                disabledView={
                  <a className="instructions-link" href="#" onClick={() => setInstructions(true)}>
                    How do I generate an App-Specific Password?
                  </a>
                }
              >
                <a className="instructions-link" href="#" onClick={() => setNewInstructions(true)}>
                  How do I generate an App-Specific Password?
                </a>
              </FeatureFlagView>
              <a
                target="_blank"
                className="manage-calendars"
                href="https://www.icloud.com/#calendar"
              >
                Manage Calendars &amp; Sharing
              </a>
            </>
          )}
        </div>
      ) : (
        <Form handleSubmit={() => addAccount(form)} autoComplete="off">
          <FeatureFlagView
            featureName={FLAG_APP_SPECIFIC_PASSWORD}
            disabledView={
              <div className="simple-instructions">
                <h6>Please Generate an App-Specific Password</h6>
                <p>
                  Due to Apple security measures, you will need to generate a one-time-use password
                  to allow Apple Calendar access.
                </p>
                <p>
                  <a className="show-instructions" href="#" onClick={() => setInstructions(true)}>
                    Click Here
                  </a>{' '}
                  for simple instructions. It&apos;s easy!
                </p>
              </div>
            }
          >
            <div className="simple-instructions new">
              <p>
                Apple requires you to create a one-time-use password for Artful Agenda to connect
                your calendar. (If you have any trouble, click{' '}
                <a
                  className="show-instructions new"
                  href="#"
                  onClick={() => setNewInstructions(true)}
                >
                  here
                </a>
                )
              </p>
              <div className="link-to-apple-instructions">
                <p>
                  <a
                    className="apple-instructions-generate"
                    target="_blank"
                    href="https://support.apple.com/en-us/102654#generate"
                  >
                    Generate your app-specific password
                  </a>
                </p>
              </div>
            </div>
          </FeatureFlagView>
          <TextInput
            className="minimal-input mobile-font-fix"
            type="email"
            name="email"
            value={form.email}
            placeholder="Apple ID"
            showLabel={false}
            autoComplete="new-username"
            onChange={updateField}
          />
          <TextInput
            className="minimal-input mobile-font-fix"
            type="password"
            name="password"
            value={form.password}
            placeholder="App-Specific Password"
            showLabel={false}
            onChange={updateField}
            autoComplete="new-password"
          />
          <Button
            className="center btn btn-default"
            isFetching={isRegistering}
            type="submit"
            text="Link Account"
            actionText="Linking"
          />
        </Form>
      )}
      <FeatureFlagView featureName={FLAG_APP_SPECIFIC_PASSWORD} disabledView={renderInstructions()}>
        {renderNewInstructions()}
      </FeatureFlagView>
    </AddAccountForm>
  );
};

AddAppleAccount.propTypes = {
  account: propTypes.object,
  form: propTypes.object.isRequired,
  addAccount: propTypes.func.isRequired,
  updateAccount: propTypes.func.isRequired,
  updateField: propTypes.func.isRequired,
  isFetching: propTypes.bool.isRequired,
  isRegistering: propTypes.bool.isRequired,
  isUpdating: propTypes.bool.isRequired,
  onClickReportProblem: propTypes.func,
};

const mapDispatchToProps = dispatch => {
  return {
    onClickReportProblem: () => dispatch(openModal(REPORT_PROBLEM_MODAL)),
  };
};

export default connect(null, mapDispatchToProps)(AddAppleAccount);
